import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { indexRolesById } from '@/lib/roleColor';
import { guildRosterKey, loadGuildDetail } from '@/lib/rosterCache';
import { useAuthStore } from '@/stores/authStore';
import type { GuildMemberRow, GuildRole } from '@/types/api';

export { guildRosterKey };

// Stable empty fallbacks — `?? []` would mint a fresh array each render, which
// would invalidate the memos below even when the query data is unchanged.
const EMPTY_MEMBERS: GuildMemberRow[] = [];
const EMPTY_ROLES: GuildRole[] = [];

/**
 * Cached guild roster (members + roles) for the user card and anywhere else
 * that needs a member's roles / the current user's standing in a guild.
 *
 * The whole roster (the detail's first page plus the rest, see
 * `loadGuildDetail`), keyed by guild id so the member panel, the user card
 * opened from a message, etc. all share one fetch. Socket events keep it
 * current in place (`rosterCache`). `enabled` is false for DM context.
 */

export interface GuildRoster {
  members: GuildMemberRow[];
  roles: GuildRole[];
  rolesById: Map<string, GuildRole>;
  ownerId: string | null;
  /** The target member row, if loaded. */
  findMember: (userId: string) => GuildMemberRow | null;
  /** The current user's member row in this guild, if any. */
  me: GuildMemberRow | null;
  isLoading: boolean;
  /** The roster fetch failed. Panels must show an error + retry instead of
   *  an endless placeholder (a dead fetch used to look like "still loading"
   *  forever). */
  isError: boolean;
  refetch: () => void;
}

export function useGuildRoster(guildId: string | null | undefined): GuildRoster {
  const myId = useAuthStore((s) => s.user?.id ?? null);

  const query = useQuery({
    queryKey: guildRosterKey(guildId ?? '∅'),
    queryFn: () => loadGuildDetail(guildId as string),
    enabled: !!guildId,
  });

  const members = query.data?.members ?? EMPTY_MEMBERS;
  const roles = query.data?.roles ?? EMPTY_ROLES;
  const ownerId = query.data?.ownerId ?? null;
  const isLoading = query.isLoading;
  const isError = query.isError;
  const refetch = query.refetch;

  // Memoize the derived map + the returned object so consumers (UserCard,
  // HeroMemberPanel, MessagesList moderation checks) get stable references when
  // the underlying query data hasn't changed — otherwise every render handed
  // out a fresh `rolesById` Map + `findMember`/`me` closures, defeating any
  // downstream memo / useMemo([roster]) and re-running permission computations.
  const rolesById = useMemo(() => indexRolesById(roles), [roles]);

  return useMemo<GuildRoster>(
    () => ({
      members,
      roles,
      rolesById,
      ownerId,
      findMember: (userId: string) => members.find((m) => m.userId === userId) ?? null,
      me: myId ? members.find((m) => m.userId === myId) ?? null : null,
      isLoading,
      isError,
      refetch: () => void refetch(),
    }),
    [members, roles, rolesById, ownerId, myId, isLoading, isError, refetch],
  );
}
